/**
 * Domain utility functions for handling the split between marketing and app domains
 */

/**
 * Get the marketing domain URL (for public pages)
 */
export const getMarketingUrl = (): string => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_SITE_URL || 'http://localhost:5173';
  }
  return import.meta.env.VITE_MARKETING_URL || 'https://hivetivity.com';
};

/**
 * Get the app domain URL (for authenticated pages)
 */
export const getAppUrl = (): string => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_SITE_URL || 'http://localhost:5173';
  }
  return import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com';
};

/**
 * Get the current domain type based on the current hostname
 */
export const getCurrentDomainType = (): 'marketing' | 'app' => {
  if (typeof window === 'undefined') return 'marketing';
  
  const hostname = window.location.hostname;
  
  if (hostname.includes('app.')) {
    return 'app';
  }
  
  return 'marketing';
};

/**
 * Check if the current user should be on the app domain
 */
export const shouldBeOnAppDomain = (userRole?: string | null): boolean => {
  if (!userRole) return false;
  
  // All authenticated users should be on app domain in production
  return ['admin', 'bizadmin', 'superadmin', 'parent', 'staff'].includes(userRole);
};

/**
 * Redirect to the appropriate domain if user is on the wrong one
 */
export const redirectToCorrectDomain = (userRole?: string | null, currentPath?: string): void => {
  if (import.meta.env.DEV) return; // Skip redirects in development
  
  const currentDomain = getCurrentDomainType();
  const shouldBeOnApp = shouldBeOnAppDomain(userRole);
  const path = currentPath || window.location.pathname + window.location.search;
  
  if (shouldBeOnApp && currentDomain === 'marketing') {
    // Authenticated user on marketing domain - redirect to app domain
    window.location.href = getAppUrl() + path;
  } else if (!shouldBeOnApp && currentDomain === 'app' && !isPublicPath(path)) {
    // Unauthenticated user on app domain accessing non-public path - redirect to marketing
    window.location.href = getMarketingUrl() + path;
  }
};

/**
 * Check if a path is a public path that can be accessed on either domain
 */
export const isPublicPath = (path: string): boolean => {
  const publicPaths = [
    '/',
    '/auth',
    '/pricing',
    '/features',
    '/blog',
    '/about-us',
    '/privacy',
    '/contact',
    '/terms-of-service',
    '/cookies',
    '/eula',
    '/dance-studios',
    '/summer-camps',
    '/swim-classes',
    '/after-school-activities',
    '/equestrian-training',
    '/music-schools',
    '/kids-gyms',
    '/play-centers',
    '/signup',
    '/business-trial',
    '/signup-cancelled',
    '/reset-password',
    '/set-password',
    '/email-verification',
    '/callback',
    '/unauthorized',
    '/account-suspended',
    '/feedback',
    '/whats-new',
    '/search-demo'
  ];
  
  // Check exact matches and pattern matches
  return publicPaths.some(publicPath => {
    if (publicPath === path) return true;
    if (publicPath.endsWith('*') && path.startsWith(publicPath.slice(0, -1))) return true;
    if (path.startsWith('/blog/') && publicPath === '/blog') return true;
    if (path.startsWith('/pricing/') && publicPath === '/pricing') return true;
    return false;
  });
};

/**
 * Get the correct URL for a given path based on whether it's public or authenticated
 */
export const getUrlForPath = (path: string, userRole?: string | null): string => {
  if (import.meta.env.DEV) {
    return (import.meta.env.VITE_SITE_URL || 'http://localhost:5173') + path;
  }
  
  if (isPublicPath(path)) {
    return getMarketingUrl() + path;
  } else if (shouldBeOnAppDomain(userRole)) {
    return getAppUrl() + path;
  } else {
    // Default to marketing for unauthenticated users
    return getMarketingUrl() + path;
  }
};